import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Grid,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  useScrollTrigger
} from '@mui/material';
import {
  Image as ImageIcon,
  CloudUpload,
  Download,
  Delete,
  Compare,
  Refresh,
  Settings,
  Compress,
  AspectRatio,
  AutoAwesome,
  KeyboardArrowUp
} from '@mui/icons-material';

function ScrollTop({ children }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    );
    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

function ImageOptimizer() {
  const [originalImages, setOriginalImages] = useState([]);
  const [optimizedImages, setOptimizedImages] = useState([]);
  const [compressionLevel, setCompressionLevel] = useState(80);
  const [maxWidth, setMaxWidth] = useState(1920);
  const [maxHeight, setMaxHeight] = useState(1080);
  const [outputFormat, setOutputFormat] = useState('jpeg');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compareImage, setCompareImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
      status: 'pending'
    }));
    
    setOriginalImages(prev => [...prev, ...newImages]);
    setError('');
    event.target.value = '';
  };

  const removeImage = (id) => {
    setOriginalImages(prev => prev.filter(img => img.id !== id));
    setOptimizedImages(prev => prev.filter(img => img.originalId !== id));
  };

  // Simple and reliable image optimization
  const optimizeImage = (image) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image(); // Use window.Image to avoid conflict
      
      img.onload = () => {
        try {
          // Create canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions
          let { width, height } = calculateNewDimensions(img.width, img.height);
          
          // Set canvas size
          canvas.width = width;
          canvas.height = height;
          
          // Fill background for JPEG
          if (outputFormat === 'jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
          }
          
          // Draw image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Get mime type and quality
          const mimeType = getMimeType();
          const quality = compressionLevel / 100;
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to create optimized image'));
                return;
              }
              
              const optimizedUrl = URL.createObjectURL(blob);
              const savings = Math.round((1 - blob.size / image.size) * 100);
              
              const optimizedImage = {
                id: Date.now() + Math.random(),
                originalId: image.id,
                file: blob,
                name: `optimized-${image.name.split('.')[0]}.${outputFormat}`,
                size: blob.size,
                type: mimeType,
                url: optimizedUrl,
                originalSize: image.size,
                savings: savings,
                width,
                height
              };
              
              console.log('Optimized image:', optimizedImage);
              resolve(optimizedImage);
            },
            mimeType,
            outputFormat === 'jpeg' || outputFormat === 'webp' ? quality : 1
          );
          
        } catch (err) {
          reject(err);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = image.url;
    });
  };

  const calculateNewDimensions = (originalWidth, originalHeight) => {
    let width = originalWidth;
    let height = originalHeight;
    
    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
    }
    
    return { width, height };
  };

  const getMimeType = () => {
    switch (outputFormat) {
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'webp': return 'image/webp';
      default: return 'image/jpeg';
    }
  };

  const processAllImages = async () => {
    if (originalImages.length === 0) {
      setError('Please upload images first');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    setOptimizedImages([]);
    
    try {
      const results = [];
      
      for (const image of originalImages) {
        try {
          console.log('Processing:', image.name);
          const optimized = await optimizeImage(image);
          results.push(optimized);
          console.log('Success:', image.name, 'savings:', optimized.savings + '%');
        } catch (err) {
          console.error('Error processing', image.name, err);
          setError(`Failed to optimize ${image.name}: ${err.message}`);
        }
      }
      
      setOptimizedImages(results);
      
      if (results.length === 0) {
        setError('No images were successfully optimized. Please try different settings.');
      } else {
        console.log('Optimization completed:', results.length, 'images processed');
      }
      
    } catch (error) {
      console.error('Process error:', error);
      setError('Failed to process images: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadImage = (image) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    optimizedImages.forEach((image, index) => {
      setTimeout(() => downloadImage(image), index * 100);
    });
  };

  const getTotalSavings = () => {
    const originalTotal = originalImages.reduce((sum, img) => sum + img.size, 0);
    const optimizedTotal = optimizedImages.reduce((sum, img) => sum + img.size, 0);
    const savings = originalTotal > 0 ? Math.round((1 - optimizedTotal / originalTotal) * 100) : 0;
    
    return {
      original: originalTotal,
      optimized: optimizedTotal,
      savings: savings
    };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const resetAll = () => {
    setOriginalImages([]);
    setOptimizedImages([]);
    setError('');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      const newImages = files.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
        status: 'pending'
      }));
      setOriginalImages(prev => [...prev, ...newImages]);
      setError('');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const totalSavings = getTotalSavings();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <div id="back-to-top-anchor" />
      <br/><br/><br/>
      <Box textAlign="center" mb={6}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
          <ImageIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
            Image Optimizer
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Compress, resize, and convert images without losing quality
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Controls Panel */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, position: 'sticky', top: 24 }}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
              <Settings color="primary" />
              Optimization Settings
            </Typography>

            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                <Compress color="action" />
                Compression: {compressionLevel}%
              </Typography>
              <Slider
                value={compressionLevel}
                onChange={(e, value) => setCompressionLevel(value)}
                min={10}
                max={100}
                step={5}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                <AspectRatio color="action" />
                Max Dimensions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Width"
                    type="number"
                    value={maxWidth}
                    onChange={(e) => setMaxWidth(parseInt(e.target.value) || 1920)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Height"
                    type="number"
                    value={maxHeight}
                    onChange={(e) => setMaxHeight(parseInt(e.target.value) || 1080)}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={maintainAspectRatio}
                    onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                  />
                }
                label="Maintain aspect ratio"
                sx={{ mt: 1 }}
              />
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                <ImageIcon color="action" />
                Output Format
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Format</InputLabel>
                <Select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  label="Format"
                >
                  <MenuItem value="jpeg">JPEG</MenuItem>
                  <MenuItem value="png">PNG</MenuItem>
                  <MenuItem value="webp">WebP</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
              <Button
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={processAllImages}
                disabled={originalImages.length === 0 || isProcessing}
                fullWidth={isMobile}
              >
                {isProcessing ? 'Processing...' : 'Optimize All'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={resetAll}
                fullWidth={isMobile}
              >
                Reset
              </Button>
            </Box>

            {optimizedImages.length > 0 && (
              <Box p={2} sx={{ bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom color="success.dark">
                  Optimization Complete
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Processed: {optimizedImages.length} images
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Total Savings: {totalSavings.savings}%
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'primary.main',
              backgroundColor: 'grey.50',
              cursor: 'pointer',
              mb: 3
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="primary">
              Drop images here or click to upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supports JPG, PNG, WebP, GIF
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              sx={{ mt: 2 }}
            >
              Select Images
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </Paper>

          {isProcessing && (
            <Box mb={3}>
              <Typography variant="body2" gutterBottom>
                Processing {originalImages.length} images... (Check console for details)
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {originalImages.length > 0 && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Images ({originalImages.length})
                </Typography>
                {optimizedImages.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={downloadAll}
                    size="small"
                  >
                    Download All
                  </Button>
                )}
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Preview</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Original Size</TableCell>
                      <TableCell>Optimized Size</TableCell>
                      <TableCell>Savings</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {originalImages.map((original) => {
                      const optimized = optimizedImages.find(img => img.originalId === original.id);
                      return (
                        <TableRow key={original.id}>
                          <TableCell>
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: 1,
                                backgroundImage: `url(${original.url})`,
                                backgroundSize: 'cover',
                                cursor: 'pointer'
                              }}
                              onClick={() => setSelectedImage(original)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {original.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatFileSize(original.size)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {optimized ? (
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                {formatFileSize(optimized.size)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {optimized && (
                              <Chip
                                label={`${optimized.savings}%`}
                                color={optimized.savings > 50 ? 'success' : optimized.savings > 20 ? 'warning' : 'default'}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              {optimized && (
                                <>
                                  <Tooltip title="Download optimized">
                                    <IconButton
                                      size="small"
                                      onClick={() => downloadImage(optimized)}
                                    >
                                      <Download />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Compare">
                                    <IconButton
                                      size="small"
                                      onClick={() => setCompareImage(original)}
                                    >
                                      <Compare />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => removeImage(original.id)}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {originalImages.length === 0 && (
            <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
              <ImageIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="grey.600">
                No Images Uploaded
              </Typography>
              <Typography variant="body2" color="grey.500">
                Upload images to start optimizing
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Compare Dialog */}
      <Dialog open={!!compareImage} onClose={() => setCompareImage(null)} maxWidth="lg" fullWidth>
        {compareImage && (
          <>
            <DialogTitle>Compare Images</DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom textAlign="center">
                    Original
                  </Typography>
                  <Box
                    component="img"
                    src={compareImage.url}
                    alt="Original"
                    sx={{ width: '100%', height: 'auto', borderRadius: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                    Size: {formatFileSize(compareImage.size)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom textAlign="center">
                    Optimized
                  </Typography>
                  {(() => {
                    const optimized = optimizedImages.find(img => img.originalId === compareImage.id);
                    return optimized ? (
                      <>
                        <Box
                          component="img"
                          src={optimized.url}
                          alt="Optimized"
                          sx={{ width: '100%', height: 'auto', borderRadius: 1 }}
                        />
                        <Typography variant="body2" color="success.main" textAlign="center" mt={1}>
                          Size: {formatFileSize(optimized.size)} ({optimized.savings}% smaller)
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Not optimized
                      </Typography>
                    );
                  })()}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCompareImage(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ScrollTop>
        <Fab color="primary" size="medium">
          <KeyboardArrowUp />
        </Fab>
      </ScrollTop>
    </Container>
  );
}

export default ImageOptimizer;